import React, { FC, useState, useEffect, useContext, MouseEvent } from 'react';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

import styled from 'styled-components/macro';
import { observer } from 'mobx-react-lite';

import { FREE_TRIAL_CTAS, FLASH } from 'config';
import {
    Grid,
    Column,
    PlansToggle,
    FlashModal,
    PlanGradient,
} from 'components';
import analytics from 'tracking/analytics';
import { media, plan_get_description, get_plan_type } from 'helpers';
import { PLANS_SWITCH__BUTTON__TOGGLE_PERIOD } from 'tracking/trackingData';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';

import { CommonStore } from 'stores';

const AnimatedGrid = styled((props) => <Grid alignCenter {...props} />)`
    &.fade-enter {
        opacity: 0;
    }

    &.fade-exit {
        opacity: 1;
    }

    &.fade-enter-active {
        opacity: 1;
    }

    &.fade-exit-active {
        opacity: 0;
    }
    &.fade-enter-active,
    &.fade-exit-active {
        transition: opacity 300ms;
    }
`;

const ColumnSortMobile = styled((props) => <Column {...props} />)<{
    $order: number;
}>`
    ${({ $order }) => media.mobile`
        order: ${$order};
    `}

    ${({ theme }) => media.toTablet`
        margin-bottom: ${theme.space.medium};
    `}
`;

const FILTERS = {
    monthly: 'monthly',
    yearly: 'yearly',
};

const MOBILE_ORDER = {
    premium: 3,
    premiumPlus: 1,
    family: 2,
};

type PlansGradientType = {
    planCta?: string;
    showToggle?: boolean;
    onPlanClick?: (e: MouseEvent<HTMLDivElement>, plan: any) => void;
    plans: any[];
    method?: 'upgrade' | 'resubscribe' | 'subscribe';
    featuresType: 'campaign' | 'payment' | 'ordinary' | 'blackFriday';
    biggerCta?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const PlansGradient: FC<PlansGradientType> = ({
    showToggle = false,
    planCta,
    onPlanClick,
    plans,
    method,
    featuresType,
    biggerCta,
}) => {
    const commonStore = useContext(CommonStore);
    const subscription = commonStore.subscription as any;

    const [filtered, setFiltered] = useState<any>([]);
    const [currentFilter, setCurrentFilter] = useState(FILTERS.yearly);

    useEffect(() => {
        setFiltered(
            showToggle ? plans.filter((p) => p.id.indexOf('year') >= 0) : plans,
        );
    }, [plans, showToggle]);

    const applyCurrentFilter = (filter: string) => {
        setCurrentFilter(filter);
        setFiltered(
            plans.filter(
                (p) =>
                    p.id.indexOf(
                        filter === FILTERS.monthly ? 'month' : 'year',
                    ) >= 0,
            ),
        );

        analytics.trackEvent({
            ...PLANS_SWITCH__BUTTON__TOGGLE_PERIOD,
            name: filter,
        });
    };

    const getDiscountValue = (plan: any) =>
        plan.discount ? i18n._(t`Save ${plan.discount}%`) : '';

    const getFeatures = (plan: any) => {
        switch (featuresType) {
            case 'campaign':
                return plan.campaign_features;
            case 'payment':
                return plan.payment_features;
            case 'ordinary':
                return plan.features;
            case 'blackFriday':
                const userPolicies = commonStore.userPolicies as any;
                const country = (commonStore.userLocation as any).country;
                const canShowBFbenefits =
                    (userPolicies && userPolicies.licensed_content) ||
                    !(userPolicies || ['JP', 'AR'].includes(country));

                return canShowBFbenefits ? plan.bf_features : plan.features;
        }
    };

    const getPlanPrints = (plan: any) => {
        if (!plan.prints) return '';

        let cta, price;

        if (plan.discount) {
            cta = currentFilter === 'yearly' ? i18n._(plan.prints) : '';
            price = '';
        } else {
            price = currentFilter === 'yearly' ? i18n._(plan.prints) : '';
            cta = plan.free_trial
                ? i18n._(t`Cancel anytime during trial`)
                : i18n._(t`Cancel anytime`);
        }

        return { cta, price };
    };

    return (
        <React.Fragment>
            {showToggle && (
                <PlansToggle
                    currentFilter={currentFilter}
                    filters={FILTERS}
                    onFilterChange={applyCurrentFilter}
                />
            )}
            <SwitchTransition>
                <CSSTransition
                    classNames="fade"
                    key={currentFilter === FILTERS.monthly ? 'month' : 'year'}
                    addEndListener={(node, done) =>
                        node.addEventListener('transitionend', done, false)
                    }
                >
                    <AnimatedGrid
                        className={
                            currentFilter === FILTERS.monthly
                                ? FILTERS.monthly
                                : FILTERS.yearly
                        }
                    >
                        {filtered.map((plan: any) => {
                            const planType = get_plan_type(plan);
                            const discountValue = getDiscountValue(plan);
                            const planPrints = getPlanPrints(plan);

                            return (
                                <ColumnSortMobile
                                    $order={MOBILE_ORDER[planType]}
                                    key={`plan-column-${plan.id}`}
                                    cols={4}
                                    tablet={6}
                                >
                                    <PlanGradient
                                        biggerCta={biggerCta}
                                        onPlanClick={(e) =>
                                            onPlanClick
                                                ? onPlanClick(e, plan)
                                                : null
                                        }
                                        currentPlan={
                                            subscription &&
                                            subscription.plan.id === plan.id
                                        }
                                        currentPlanText={i18n._(
                                            t`Your membership`,
                                        )}
                                        planType={planType}
                                        cta={
                                            planCta
                                                ? planCta
                                                : plan.free_trial
                                                ? i18n._(FREE_TRIAL_CTAS)
                                                : i18n._(t`Subscribe now`)
                                        }
                                        price={plan.price.display}
                                        fullprice={
                                            plan.fullprice &&
                                            plan.fullprice.display
                                        }
                                        features={getFeatures(plan)}
                                        description={plan_get_description(plan)}
                                        method={method}
                                        headline={
                                            discountValue
                                                ? discountValue
                                                : plan.free_trial
                                                ? i18n._(t`Free trial`)
                                                : ''
                                        }
                                        pricePrints={
                                            planPrints && planPrints.price
                                        }
                                        ctaPrints={planPrints && planPrints.cta}
                                    />
                                </ColumnSortMobile>
                            );
                        })}
                    </AnimatedGrid>
                </CSSTransition>
            </SwitchTransition>

            <FlashModal name={FLASH.paypal_payment_error} />
        </React.Fragment>
    );
};

export default observer(PlansGradient);
